import { isNullOrEmpty, isValidEmail } from "@utils/string";

import { birthyearRegex, phoneRegex, stateRegex, zipCodeRegex } from "./formConstants";
import formErrors from "./formErrors.json";

type ErrorType = "empty" | "invalid";

type FieldState = {
  value: unknown;
  required: boolean;
  checkValidIfNotEmpty?: boolean;
};

type FormState = Record<string, FieldState>;
type ValidationErrors = Record<string, unknown>;
type FormErrorsCatalog = typeof formErrors;

/**
 * Validate form state against formErrors catalog
 * Pure: never mutates input objects
 */
export function validate(formState: FormState, currentErrors: ValidationErrors): ValidationErrors {
  return Object.entries(formState).reduce<ValidationErrors>(
    (acc, [fieldName, fieldState]) => {
      const fieldErrorMap = (formErrors as FormErrorsCatalog)[fieldName];
      if (!fieldErrorMap) return acc;

      const { value, required, checkValidIfNotEmpty } = fieldState;

      if (required) {
        const result = handleRequiredField(fieldName, value, fieldErrorMap);
        return Object.keys(result).length > 0 ? { ...acc, ...result } : clearError(acc, fieldName);
      }

      if (shouldValidateOptional(value, checkValidIfNotEmpty)) {
        const result = handleOptionalField(fieldName, value, fieldErrorMap);
        // ⬇️ Rensa gammalt fel om optional fältet är giltigt
        return Object.keys(result).length > 0 ? { ...acc, ...result } : clearError(acc, fieldName);
      }

      // optional + tomt → rensa
      return clearError(acc, fieldName);
    },
    { ...currentErrors }
  );
}

function handleRequiredField(
  fieldName: string,
  value: unknown,
  fieldErrorMap: Record<string, unknown>
): ValidationErrors {
  // Order is enforced: "empty" → "invalid".
  if (hasErrorType(fieldErrorMap, "empty") && !isValidField(fieldName, value as string, "empty")) {
    return { [fieldName]: fieldErrorMap["empty"] };
  }
  if (
    hasErrorType(fieldErrorMap, "invalid") &&
    !isValidField(fieldName, value as string, "invalid")
  ) {
    return { [fieldName]: fieldErrorMap["invalid"] };
  }
  return {}; // valid, no errors
}

function handleOptionalField(
  fieldName: string,
  value: unknown,
  fieldErrorMap: Record<string, unknown>
): ValidationErrors {
  const errorType: ErrorType = "invalid";
  if (
    hasErrorType(fieldErrorMap, errorType) &&
    !isValidField(fieldName, value as string, errorType)
  ) {
    return { [fieldName]: fieldErrorMap[errorType] };
  }
  return {};
}

function shouldValidateOptional(value: unknown, check?: boolean): boolean {
  if (!check) return false;
  // For booleans (approved/sendCopy), we DO want to validate when flagged.
  if (typeof value === "boolean") return true;
  // For strings/numbers, validate only when non-empty (stringified check is fine here).
  return !isNullOrEmpty(String(value));
}

function clearError(errors: ValidationErrors, fieldName: string): ValidationErrors {
  if (!errors[fieldName]) return errors;
  const copy = { ...errors };
  delete copy[fieldName];
  return copy;
}

function hasErrorType(map: Record<string, unknown>, key: ErrorType): boolean {
  return Object.prototype.hasOwnProperty.call(map, key);
}

/**
 * Check whether a single field is valid for a given error type
 */
function isValidField(
  fieldName: string,
  fieldValue: string | boolean | number,
  errorType: ErrorType
): boolean {
  if (errorType === "empty") {
    return typeof fieldValue !== "boolean" ? !isNullOrEmpty(String(fieldValue)) : false;
  }

  // errorType === "invalid"
  switch (fieldName) {
    case "email":
      return typeof fieldValue !== "boolean" && isValidEmail(String(fieldValue));

    case "zipCode":
      return typeof fieldValue !== "boolean" && zipCodeRegex.test(String(fieldValue));

    case "state":
      return typeof fieldValue !== "boolean" && stateRegex.test(String(fieldValue));

    case "phone":
      return typeof fieldValue !== "boolean" && phoneRegex.test(String(fieldValue));

    case "approved":
      return Boolean(fieldValue);

    // Tightened rule: amount is valid only if it's a finite number > 0
    case "amount":
      return typeof fieldValue === "number" && Number.isFinite(fieldValue) && fieldValue > 0;

    case "birth":
      return typeof fieldValue !== "boolean" && birthyearRegex.test(String(fieldValue));

    // Must be a boolean if present
    case "sendCopy":
      return typeof fieldValue === "boolean";

    default:
      // Unknown fields are considered valid here; upstream decides whether to validate.
      return true;
  }
}

/**
 * Smoothly scroll a ref into view and focus it
 */
export function scrollIntoView(
  ref: React.RefObject<HTMLElement>,
  scrollPosition: ScrollLogicalPosition = "center"
) {
  ref?.current?.scrollIntoView({ behavior: "smooth", block: scrollPosition });
  setTimeout(() => ref?.current?.focus(), 700);
}
